import React from 'react';
import { css } from '@compiled/react';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { BACKGROUND_SCRIPT_APPS_FORGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import {
	FORGE_MODULE_BACKGROUND_SCRIPT,
	FORGE_MODULE_PAGE_BANNER,
} from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import {
	BackgroundScriptForgeApp,
	PageBannerForgeApp,
} from '@confluence/forge-ui/entry-points/apps.ts';
import { getContentType } from '@confluence/forge-ui/entry-points/contentType.ts';
import { getAppId } from '@confluence/forge-ui/entry-points/appData.ts';
import { recordNonContentAppKeys } from '@confluence/browser-metrics/entry-points/NonContentAppKeys.ts';
import { LazyForgeUIExtensions } from '@confluence/forge-ui/entry-points/lazy.ts';
import { type ForgeUIExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { usePageState } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';
import { useSpaceIdentifiers } from '@confluence/space-utils/entry-points/useSpaceIdentifiers.ts';

// This is exported solely for testing purposes

const invisible = css({ display: 'none' });
export const RenderForgeAppsAsBackgroundScripts = (
	forgeModules: ReadonlyArray<ForgeUIExtensionType>,
	loading: boolean,
	error?: Error,
): React.JSX.Element | React.ReactNode[] | null => {
	const isLivePage = useIsCurrentPageLive();
	const [{ contentId, routeName, spaceKey: spaceAlias }] = usePageState();
	const {
		spaceId,
		spaceKey,
		loading: spaceLoading,
	} = useSpaceIdentifiers(
		fg('confluence_forge_background_script_space_context_fix') ? (spaceAlias ?? '') : undefined,
	);

	if (loading) return null;

	if (error) {
		if (!isErrorMarkedAsHandled(error)) {
			return <ErrorDisplay error={error} />;
		}
		return null;
	}

	// Instrument the presence of non-content 3P background-script apps so their
	// keys are folded into the page-load `connectAppKeys` telemetry.
	recordNonContentAppKeys(
		'background-script',
		forgeModules.map((app) => app?.id),
	);

	if (spaceAlias && spaceLoading && fg('confluence_forge_background_script_space_context_fix')) {
		return null;
	}

	return forgeModules.map(
		withErrorBoundary({
			attribution: Attribution.ECOSYSTEM,
		})((app: ForgeUIExtensionType) => (
			<ErrorBoundary
				key={getAppId(app)}
				attributes={{ appId: getAppId(app) }}
				attribution={Attribution.ECOSYSTEM}
			>
				{/* Render the background script Forge apps inside an invisible div - we don't want to show any UI for these apps */}
				<div css={invisible}>
					<ExperienceStart name={BACKGROUND_SCRIPT_APPS_FORGE_EXPERIENCE} id={getAppId(app)} />
					<BackgroundScriptForgeApp
						app={app}
						// The background script module is rendered at the MainLayout level. Certain page navigations like navigating between pages within the same space will not trigger a re-invocation of the Forge app.
						// Adding the pageRoute to the extensionData will allow the Forge app to re-render when the route changes
						extensionData={{
							type: FORGE_MODULE_BACKGROUND_SCRIPT,
							...(contentId
								? {
										content: {
											id: contentId,
											type: getContentType(routeName),
											subtype: isLivePage ? 'live' : null,
										},
									}
								: {}),
							...(spaceKey || spaceAlias
								? {
										space: {
											id: spaceId,
											key: spaceKey || spaceAlias,
										},
									}
								: {}),
						}}
					/>
				</div>
			</ErrorBoundary>
		)),
	);
};

const BackgroundScriptAppsWithSpaceKey = (): React.JSX.Element => {
	const [{ spaceKey }] = usePageState();

	// Reset the lookup on space changes: it can retain the previous space with loading=false.
	return (
		<LazyForgeUIExtensions
			key={spaceKey}
			moduleType={FORGE_MODULE_BACKGROUND_SCRIPT}
			render={RenderForgeAppsAsBackgroundScripts}
		/>
	);
};

export const RenderBackgroundScriptApps: React.MemoExoticComponent<() => React.JSX.Element> =
	React.memo(
		(): React.JSX.Element =>
			fg('confluence_forge_background_script_space_context_fix') ? (
				<BackgroundScriptAppsWithSpaceKey />
			) : (
				<LazyForgeUIExtensions
					moduleType={FORGE_MODULE_BACKGROUND_SCRIPT}
					render={RenderForgeAppsAsBackgroundScripts}
				/>
			),
	);

type PageBannerForgeAppWithErrorBoundaryProps = React.ComponentProps<typeof PageBannerForgeApp>;

// Keep this component type stable so only an explicit key change remounts the app.
const PageBannerForgeAppWithErrorBoundary = withErrorBoundary({
	attribution: Attribution.ECOSYSTEM,
})(({ app, ...props }: PageBannerForgeAppWithErrorBoundaryProps) => (
	<ErrorBoundary attributes={{ appId: getAppId(app) }} attribution={Attribution.ECOSYSTEM}>
		<PageBannerForgeApp app={app} {...props} />
	</ErrorBoundary>
));

// This is exported solely for testing purposes
export const RenderForgeAppsAsPageBanners = (
	forgeModules: ReadonlyArray<ForgeUIExtensionType>,
	loading: boolean,
	error?: Error,
): React.JSX.Element | React.ReactNode[] | null => {
	const [{ contentId, routeName, spaceKey: spaceAlias }] = usePageState();
	const { spaceId, spaceKey } = useSpaceIdentifiers();
	const isLivePage = useIsCurrentPageLive();

	if (loading) return null;

	if (error) {
		if (!isErrorMarkedAsHandled(error)) {
			return <ErrorDisplay error={error} />;
		}
		return null;
	}

	// Instrument the presence of non-content 3P page-banner apps so their keys are
	// folded into the page-load `connectAppKeys` telemetry.
	recordNonContentAppKeys(
		'page-banner',
		forgeModules.map((app) => app?.id),
	);

	// A shared preset height is only safe for one Custom UI banner; UI Kit does not use an iframe.
	const shouldPresetHeight =
		forgeModules.length === 1 &&
		forgeModules[0]?.properties?.resource != null &&
		forgeModules[0]?.properties?.render !== 'native';
	const pageBannerProps: Omit<PageBannerForgeAppWithErrorBoundaryProps, 'app'> = {
		shouldPresetHeight,
		extensionData: {
			type: FORGE_MODULE_PAGE_BANNER,
			...(contentId
				? {
						content: {
							id: contentId,
							type: getContentType(routeName),
							subtype: isLivePage ? 'live' : null,
						},
					}
				: {}),
			...(spaceKey || spaceAlias
				? {
						space: {
							id: spaceId,
							key: spaceKey || spaceAlias,
						},
					}
				: {}),
		},
	};

	return forgeModules.map((app) => (
		<PageBannerForgeAppWithErrorBoundary
			key={`${getAppId(app)}:${contentId ?? ''}`}
			app={app}
			{...pageBannerProps}
		/>
	));
};

export const RenderPageBannerForgeApps: React.MemoExoticComponent<() => React.JSX.Element> =
	React.memo((): React.JSX.Element => {
		return (
			<LazyForgeUIExtensions
				moduleType={FORGE_MODULE_PAGE_BANNER}
				render={RenderForgeAppsAsPageBanners}
			/>
		);
	});
